<template>
    <div class="body bank-app">
        <Navbar :show-back-button="true" @back="navigateToDashboard"/>
        
        <div class="section-header">
            <!-- <button class="back-arrow" @click="navigateToDashboard">
                →
            </button> -->
            <h4 class="search-title">
                <span class="search-icon">🔍</span>
                {{ $t('employer.customerSearch.title') }}
            </h4>
        </div>
        
        <!-- Search Section -->
        <div class="search-section">
            <div class="search-card">
                <div class="search-header">
                    
                    <p class="search-subtitle">
                        {{ $t('employer.customerSearch.inputHint') }}
                    </p>
                </div>
                
                <div class="search-input-group">
                    <div class="search-input-container">
                        <input 
                            type="text" 
                            v-model="searchTerm"
                            @keyup.enter="performSearch"
                            :placeholder="$t('employer.customerSearch.inputPlaceholder')"
                            class="search-input"
                            :disabled="loading"
                        />
                        <span class="input-icon">👤</span>
                        <button 
                            class="clear-search-btn" 
                            @click="clearSearch"
                            v-if="searchTerm"
                            title="پاک کردن"
                        >
                            ✕
                        </button>
                    </div>
                    
                    <button 
                        class="search-btn"
                        @click="performSearch"
                        :disabled="!searchTerm.trim() || loading"
                    >
                        <span v-if="!loading">
                            <span class="btn-icon">🔍</span>
                            {{ $t('employer.customerSearch.searchButton') }}
                        </span>
                        <span v-else>
                            <span class="loading-spinner-small"></span>
                            {{ $t('employer.customerSearch.searching') }}
                        </span>
                    </button>
                </div>
            </div>
        </div>
        
        <!-- Results Section -->
        <div class="results-section" v-if="showResults">
            <div class="results-header">
                <h4 class="results-title">
                    <span class="results-icon">📋</span>
                    {{ $t('employer.customerSearch.results.title') }}
                    <span class="results-count" :class="{ 'has-results': customers.length > 0 }">
                        ({{ customers.length }} {{ $t('employer.customerSearch.results.count') }})
                    </span>
                </h4>
                
                <div class="results-actions">
                    <button 
                        class="refresh-results-btn"
                        @click="performSearch"
                        :disabled="loading"
                        title="بروزرسانی نتایج"
                    >
                        🔄
                    </button>
                    <button 
                        class="clear-results-btn"
                        @click="clearResults"
                        v-if="customers.length > 0"
                        title="پاک کردن نتایج"
                    >
                        ✕ {{ $t('employer.customerSearch.results.clearResults') }}
                    </button>
                </div>
            </div>
            
            <!-- Loading State -->
            <div v-if="loading" class="loading-results">
                <div class="loading-spinner"></div>
                <span>{{ $t('employer.customerSearch.results.loading') }}</span>
            </div>
            
            <!-- Error State -->
            <div v-else-if="error" class="error-results">
                <div class="error-icon">⚠️</div>
                <h4>{{ $t('employer.customerSearch.results.errorTitle') }}</h4>
                <p>{{ error }}</p>
                <button @click="performSearch" class="retry-btn">
                    {{ $t('employer.customerSearch.results.retry') }}
                </button>
            </div>
            
            <!-- No Results State -->
            <div v-else-if="customers.length === 0 && searchPerformed" class="no-results">
                <div class="empty-icon">👥</div>
                <h4>{{ $t('employer.customerSearch.results.notFoundTitle') }}</h4>
                <p>{{ $t('employer.customerSearch.results.notFoundMessage', { term: searchTerm }) }}</p>
                <p class="suggestion">
                    {{ $t('employer.customerSearch.results.suggestion') }}
                </p>
            </div>
            
            <!-- Results Grid -->
            <div v-else-if="customers.length > 0" class="customers-grid">
                <div 
                    v-for="(customer) in customers" 
                    :key="customer.id_subcode"
                    class="customer-card"
                    @click="selectCustomer(customer)"
                    :class="{ 'selected': selectedCustomer?.id_subcode === customer.id_subcode }"
                >
                    <div class="customer-card-header">
                        <div class="customer-avatar">
                            {{ getInitials(customer.name) }}
                        </div>
                        <div class="customer-info">
                            <h5 class="customer-name">{{ customer.name }}</h5>
                        </div>
                        <div class="customer-actions">
                            <button class="select-customer-btn" @click.stop="selectCustomer(customer)">
                                <span class="btn-icon">→</span>
                                {{ $t('employer.customerSearch.customerCard.select') }}
                            </button>
                        </div>
                    </div>
                    
                    <div class="customer-card-body">
                        <div class="customer-details">
                            <div class="detail-row" v-if="customer.phone">
                                <span class="detail-label">📱 {{ $t('employer.customerSearch.customerCard.phone') }}</span>
                                <span class="detail-value" style="direction: ltr;">{{ formatPhone(customer.phone) }}</span>
                            </div>
                            <div class="detail-row">
                                <span class="detail-label">🆔 {{ $t('employer.customerSearch.customerCard.code') }}</span>
                                <span class="detail-value">{{ customer.id_subcode }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import Navbar from '@/components/Navbar.vue'
import { employerService } from '@/services/api'
import { useAuthStore } from '@/stores/auth'

export default {
    name: 'CustomerSearchView',
    components: {
        Navbar
    },
    setup() {
        const router = useRouter()
        
        // State
        const searchTerm = ref('')
        const loading = ref(false)
        const error = ref(null)
        const customers = ref([])
        const selectedCustomer = ref(null)
        const searchPerformed = ref(false)
        const showDebug = ref(true) // Set to false in production
        const authStore = useAuthStore()
        
        // Computed properties
        const showResults = computed(() => {
            return searchPerformed.value && !loading.value
        })
        
        const currentDate = computed(() => {
            return new Date().toLocaleDateString('fa-IR')
        })
        
        // Methods
        const navigateToDashboard = () => {
            router.push('/accountant/dashboard')
        }
        
        const performSearch = async () => {
            if (!searchTerm.value.trim()) {
                error.value = 'لطفاً عبارت جستجو را وارد کنید'
                return
            }
            
            if (searchTerm.value.trim().length < 2) {
                error.value = 'لطفاً حداقل ۲ حرف وارد کنید'
                return
            }
            
            loading.value = true
            error.value = null
            searchPerformed.value = true
            
            try {
                console.log('🔍 Searching customers with term:', searchTerm.value)
                
                const response = await employerService.searchCustomers(searchTerm.value.trim())
                
                if (response.data.success) {
                    customers.value = response.data.data
                    console.log(`✅ Found ${customers.value.length} customers`)
                    
                    if (customers.value.length === 0) {
                        error.value = response.data.message || 'مشتری یافت نشد'
                    }
                } else {
                    error.value = response.data.message || 'خطا در جستجو'
                    customers.value = []
                }
            } catch (err) {
                console.error('❌ Error searching customers:', err)
                error.value = err.response?.data?.detail || err.message || 'خطا در اتصال به سرور'
                customers.value = []
            } finally {
                loading.value = false
            }
        }
        
        const clearSearch = () => {
            searchTerm.value = ''
            clearResults()
        }
        
        const clearResults = () => {
            customers.value = []
            selectedCustomer.value = null
            searchPerformed.value = false
            error.value = null
        }
        
        const clearSelection = () => {
            selectedCustomer.value = null
        }
        
        const selectCustomer = (customer) => {
            selectedCustomer.value = customer
            console.log('✅ Selected customer:', customer.id_subcode, authStore.getSubcode)
            authStore.SetAccountantOperatorAndCustomerId(authStore.getSubcode, customer.id_subcode)


            router.push('/accountant/kala-store')
        }
        
        const getInitials = (name) => {
            if (!name) return '؟'
            const parts = name.split(' ')
            if (parts.length >= 2) {
                return (parts[0][0] + parts[1][0]).toUpperCase()
            }
            return name.substring(0, 2).toUpperCase()
        }
        
        const formatPhone = (phone) => {
            if (!phone) return 'ندارد'
            // Format Iranian phone numbers
            const cleaned = phone.replace(/\D/g, '')
            if (cleaned.length === 11 && cleaned.startsWith('09')) {
                return cleaned.replace(/(\d{4})(\d{3})(\d{4})/, '$1 $2 $3')
            }
            return phone
        }
        
        const viewCustomerTransactions = () => {
            if (selectedCustomer.value) {
                console.log('📊 Viewing transactions for customer:', selectedCustomer.value.id_subcode)
                // Navigate to transactions page or show modal
                alert(`در حال بارگذاری تراکنش‌های مشتری: ${selectedCustomer.value.name}`)
            }
        }
        
        const viewCustomerProfile = () => {
            if (selectedCustomer.value) {
                console.log('👤 Viewing profile for customer:', selectedCustomer.value.id_subcode)
                // Navigate to profile page or show modal
                alert(`در حال بارگذاری پروفایل مشتری: ${selectedCustomer.value.name}`)
            }
        }
        
        // Initialize if needed
        onMounted(() => {
            // Check if there's a saved search term
            const savedSearch = localStorage.getItem('lastSearchTerm')
            if (savedSearch) {
                searchTerm.value = savedSearch
            }
        })
        
        return {
            // State
            searchTerm,
            loading,
            error,
            customers,
            selectedCustomer,
            searchPerformed,
            showDebug,
            
            // Computed
            showResults,
            currentDate,
            
            // Methods
            navigateToDashboard,
            performSearch,
            clearSearch,
            clearResults,
            clearSelection,
            selectCustomer,
            getInitials,
            formatPhone,
            viewCustomerTransactions,
            viewCustomerProfile
        }
    }
}
</script>

<style scoped>
.bank-app {
    min-height: 100vh;
    background: var(--bg);
    padding: 20px;
    direction: rtl;
}

/* Section Header */
.section-header {
    background: var(--card-bg);
    border-radius: 15px;
    padding: 20px;
    margin-bottom: 20px;
    margin-top: 20px;
    box-shadow: var(--shadow-lg);
    position: relative;
}

.back-arrow {
    position: absolute;
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
    border: none;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    font-size: 20px;
    cursor: pointer;
    transition: all 0.3s ease;
    background: var(--card-bg);
    color: var(--text);
}

.back-arrow:hover {
    transform: translateY(-50%) scale(1.1);
    box-shadow: var(--shadow-lg);
}

.section-title {
    text-align: center;
    color: var(--text);
    font-size: 24px;
    font-weight: 700;
    margin: 0;
}

/* Search Section */
.search-section {
    margin-bottom: 30px;
}

.search-card {
    background: var(--card-bg);
    border-radius: 15px;
    padding: 25px;
    box-shadow: var(--shadow-md);
    border: 1px solid var(--border);
}

.search-header {
    margin-bottom: 25px;
    text-align: center;
}

.search-title {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: var(--text);
    font-size: 22px;
}

.search-icon {
    font-size: 24px;
    color: var(--primary);
}

.search-subtitle {
    color: var(--subtext);
    font-size: 14px;
    margin: 0;
    opacity: 0.8;
}

/* Search Input Group */
.search-input-group {
    display: flex;
    gap: 15px;
    margin-bottom: 20px;
}

@media (max-width: 768px) {
    .search-input-group {
        flex-direction: column;
    }
}

.search-input-container {
    flex: 1;
    position: relative;
}

.search-input {
    width: 100%;
    padding: 15px 50px 15px 45px;
    border: 2px solid var(--border);
    border-radius: 12px;
    font-size: 16px;
    transition: all 0.3s ease;
    background: var(--card-bg);
    color: var(--text);
}

.search-input:focus {
    outline: none;
    border-color: var(--primary);
    background: var(--card-bg);
    box-shadow: 0 0 0 3px var(--ring);
}

.search-input::placeholder {
    color: var(--subtext);
}

.input-icon {
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 20px;
    color: var(--subtext);
}

.clear-search-btn {
    position: absolute;
    left: 15px;
    top: 50%;
    transform: translateY(-50%);
    background: var(--red);
    color: var(--card-bg);
    border: none;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}

.clear-search-btn:hover {
    background: var(--primary-hover);
    transform: translateY(-50%) scale(1.1);
}

/* Search Button */
.search-btn {
    padding: 10px 30px;
    background: var(--primary);
    color: var(--card-bg);
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 10px;
    white-space: nowrap;
    min-width: 120px;
    justify-content: center;
}

.search-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    background: var(--primary-hover);
    box-shadow: var(--shadow-md);
}

.search-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-icon {
    font-size: 18px;
}

/* Small Spinner */
.loading-spinner-small {
    width: 20px;
    height: 20px;
    border: 3px solid var(--disabled);
    border-top: 3px solid var(--card-bg);
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

/* Search Tips */
.search-tips {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    justify-content: center;
    padding-top: 20px;
    border-top: 1px solid var(--border);
}

.tip-item {
    font-size: 13px;
    color: var(--subtext);
    background: var(--bg);
    padding: 6px 12px;
    border-radius: 20px;
    display: flex;
    align-items: center;
    gap: 5px;
}

/* Results Section */
.results-section {
    margin-bottom: 45px;
}

.results-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    background: var(--card-bg);
    padding: 15px 20px;
    border-radius: 15px;
    box-shadow: var(--shadow-md);
}

.results-title {
    display: flex;
    align-items: center;
    gap: 10px;
    color: var(--text);
    font-size: 18px;
    margin: 0;
}

.results-icon {
    font-size: 20px;
    color: var(--primary);
}

.results-count {
    font-size: 14px;
    color: var(--subtext);
}

.results-count.has-results {
    color: var(--primary);
    font-weight: 600;
}

.results-actions {
    display: flex;
    gap: 10px;
}

.refresh-results-btn,
.clear-results-btn {
    padding: 8px 15px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--card-bg);
}

.refresh-results-btn {
    background: var(--primary);
}

.refresh-results-btn:hover:not(:disabled) {
    background: var(--primary-hover);
    transform: translateY(-2px);
}

.clear-results-btn {
    background: var(--red);
}

.clear-results-btn:hover {
    background: var(--primary-hover);
    transform: translateY(-2px);
}

/* Loading Results */
.loading-results {
    padding: 40px;
    text-align: center;
    background: var(--card-bg);
    border-radius: 15px;
    margin: 20px 0;
}

.loading-spinner {
    width: 50px;
    height: 50px;
    border: 5px solid var(--disabled);
    border-top: 5px solid var(--primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 20px;
}

/* Error Results */
.error-results {
    padding: 40px;
    text-align: center;
    background: var(--card-bg);
    border-radius: 15px;
    margin: 20px 0;
}

.error-icon {
    font-size: 48px;
    margin-bottom: 20px;
    color: var(--red);
}

.error-results h4 {
    color: var(--red);
    margin-bottom: 10px;
}

.error-results p {
    color: var(--subtext);
    margin-bottom: 20px;
}

.retry-btn {
    padding: 10px 25px;
    background: var(--primary);
    color: var(--card-bg);
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
}

.retry-btn:hover {
    transform: translateY(-2px);
    background: var(--primary-hover);
    box-shadow: var(--shadow-md);
}

/* No Results */
.no-results {
    padding: 60px 20px;
    text-align: center;
    background: var(--card-bg);
    border-radius: 15px;
    margin: 20px 0;
}

.empty-icon {
    font-size: 60px;
    color: var(--disabled);
    margin-bottom: 20px;
}

.no-results h4 {
    color: var(--text);
    margin-bottom: 10px;
}

.no-results p {
    color: var(--subtext);
    margin-bottom: 10px;
}

.suggestion {
    font-size: 14px;
    color: var(--subtext);
}

/* Customers Grid */
.customers-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 20px;
    margin-bottom: 30px;
}

@media (max-width: 768px) {
    .customers-grid {
        grid-template-columns: 1fr;
    }
}

/* Customer Card */
.customer-card {
    background: var(--card-bg);
    border-radius: 15px;
    overflow: hidden;
    box-shadow: var(--shadow-md);
    transition: all 0.3s ease;
    cursor: pointer;
    border: 2px solid transparent;
}

.customer-card:hover {
    transform: translateY(-5px);
    box-shadow: var(--shadow-lg);
    border-color: var(--primary);
}

.customer-card.selected {
    border-color: var(--green);
    box-shadow: 0 8px 25px var(--green);
}

.customer-card-header {
    padding: 20px;
    background: var(--primary);
    color: var(--card-bg);
    display: flex;
    align-items: center;
    gap: 15px;
}

.customer-avatar {
    width: 50px;
    height: 50px;
    background: var(--hover);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    font-weight: bold;
    flex-shrink: 0;
}

.customer-info {
    flex: 1;
}

.customer-name {
    margin: 0 0 5px 0;
    font-size: 18px;
    font-weight: 700;
}

.customer-badges {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.customer-id,
.customer-type {
    background: var(--hover);
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
}

.customer-actions {
    flex-shrink: 0;
}

.select-customer-btn {
    padding: 8px 15px;
    background: var(--card-bg);
    color: var(--primary);
    border: 1px solid var(--border);
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 5px;
}

.select-customer-btn:hover {
    background: var(--hover);
    transform: translateY(-2px);
}

/* Card Body */
.customer-card-body {
    padding: 20px;
}

.customer-details {
    margin-bottom: 20px;
}

.detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid var(--border);
}

.detail-row:last-child {
    border-bottom: none;
}

.detail-label {
    color: var(--subtext);
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.detail-value {
    color: var(--text);
    font-size: 14px;
    font-weight: 600;
}

.customer-stats {
    display: flex;
    justify-content: space-between;
    background: var(--bg);
    padding: 12px 15px;
    border-radius: 10px;
}

.stat-item {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.stat-label {
    color: var(--subtext);
    font-size: 12px;
}

.stat-value {
    color: var(--text);
    font-size: 14px;
    font-weight: 600;
}

/* Selected Customer */
.selected-customer-section {
    margin-top: 30px;
}

.selected-customer-card {
    background: var(--card-bg);
    border-radius: 15px;
    overflow: hidden;
    border: 2px solid var(--green);
    box-shadow: var(--shadow-md);
}

.selected-header {
    padding: 20px;
    background: var(--green);
    color: var(--card-bg);
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.close-selected-btn {
    background: var(--hover);
    color: var(--card-bg);
    border: none;
    width: 35px;
    height: 35px;
    border-radius: 50%;
    font-size: 18px;
    cursor: pointer;
    transition: all 0.3s ease;
}

.close-selected-btn:hover {
    background: var(--hover);
    transform: rotate(90deg);
}

.selected-details {
    padding: 30px;
    display: flex;
    gap: 30px;
    align-items: center;
}

@media (max-width: 768px) {
    .selected-details {
        flex-direction: column;
        text-align: center;
    }
}

.selected-avatar-large {
    width: 100px;
    height: 100px;
    background: var(--green);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 32px;
    font-weight: bold;
    color: var(--card-bg);
}

.selected-name {
    color: var(--text);
    font-size: 24px;
    margin: 0 0 15px 0;
    font-weight: 700;
}

.meta-item {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--subtext);
    font-size: 15px;
    background: var(--bg);
    padding: 8px 15px;
    border-radius: 20px;
}

.selected-actions {
    display: flex;
    gap: 15px;
    flex-wrap: wrap;
    margin-bottom: 20px;
}

.action-btn {
    padding: 12px 20px;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 10px;
}

.action-btn.primary {
    background: var(--primary);
    color: var(--card-bg);
}

.action-btn.secondary {
    background: var(--green);
    color: var(--card-bg);
}

.action-btn.outline {
    background: var(--card-bg);
    color: var(--text);
    border: 2px solid var(--border);
}

.action-btn:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
}

.debug-info {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px dashed var(--border);
}

.debug-text {
    color: var(--subtext);
    font-size: 13px;
    margin: 5px 0;
}

/* Info Footer */
.info-footer {
    margin-top: 30px;
}

.info-card {
    background: var(--card-bg);
    border-radius: 15px;
    padding: 25px;
    box-shadow: var(--shadow-md);
}

.info-title {
    color: var(--text);
    font-size: 18px;
    margin: 0 0 15px 0;
    display: flex;
    align-items: center;
    gap: 10px;
}

.info-list li {
    padding: 8px 0;
    color: var(--subtext);
    font-size: 14px;
    position: relative;
    padding-right: 20px;
}

.info-list li:before {
    content: "•";
    position: absolute;
    right: 0;
    color: var(--primary);
    font-size: 18px;
}
</style>